/**
 * Utility functions and constants for Impact Analysis
 */

// Map impact types to human-readable labels
export const IMPACT_TYPE_LABELS = {
  'pkb_entry': 'Story (PRD)',
  'test_case': 'Test Case',
  'bug': 'Bug',
  'task': 'Task',
  'feature': 'Feature'
};

// Map impact levels to colors
export const IMPACT_LEVEL_COLORS = {
  'High': 'error',
  'Medium': 'warning',
  'Low': 'success',
  'Possible': 'info'
};

/**
 * Extract JIRA key from PKB entry content
 */
export function extractJiraKey(item) {
  if (!item) return null;
  
  // Try different possible locations
  const content = item.content || {};
  const metadata = content.metadata || {};
  
  // Check external_key first (most common)
  if (content.external_key) {
    return content.external_key;
  }
  
  // Check metadata.id (JIRA:EA-22 format)
  if (metadata.id && typeof metadata.id === 'string') {
    const match = metadata.id.match(/JIRA:(.+)/);
    if (match) return match[1];
    // Sometimes it's just the key
    if (metadata.id.match(/^[A-Z][A-Z0-9_]+-\d+$/)) {
      return metadata.id;
    }
  }
  
  // Check jira_issue_key in content
  if (content.jira_issue_key) {
    return content.jira_issue_key;
  }
  
  return null;
}

/**
 * Get JIRA URL for an issue key
 */
export function getJiraUrl(jiraKey, jiraBaseUrl) {
  if (!jiraKey || !jiraBaseUrl) return null;
  return `${jiraBaseUrl.replace(/\/$/, '')}/browse/${jiraKey}`;
}

/**
 * Format evidence into matched clauses and explanation
 */
export function formatEvidence(impact, sourceTitle) {
  const evidence = {
    matchedClauses: [],
    designClauses: [],
    explanation: null,
    hasDesignEvidence: Boolean(
      impact.has_figma_evidence ||
      (impact.evidence_sources || []).includes('figma_design') ||
      (impact.matched_sections || []).includes('designs')
    )
  };
  
  // Extract acceptance criteria from evidence spans
  const evidenceSpans = impact.evidence_spans || [];
  const designEvidenceSpans = impact.design_evidence_spans || [];
  const matchedSections = impact.matched_sections || [];
  
  // If we have acceptance_criteria in matched sections, try to extract ACs
  if (matchedSections.includes('acceptance_criteria') && evidenceSpans.length > 0) {
    // Use evidence spans as matched clauses
    evidence.matchedClauses = evidenceSpans.slice(0, 3).map((span, idx) => ({
      id: `AC${idx + 1}`,
      text: span.text || ''
    }));
  } else if (evidenceSpans.length > 0) {
    // Fallback: use evidence spans as matched clauses
    evidence.matchedClauses = evidenceSpans.slice(0, 3).map((span, idx) => ({
      id: `E${idx + 1}`,
      text: span.text || ''
    }));
  }

  if (designEvidenceSpans.length > 0) {
    evidence.designClauses = designEvidenceSpans.slice(0, 2).map((span, idx) => ({
      id: `D${idx + 1}`,
      text: span.text || ''
    }));
  } else if (matchedSections.includes('designs')) {
    evidence.designClauses = [{
      id: 'D1',
      text: 'Design/Figma content contributed to this match.'
    }];
  }
  
  // Prefer v2 relationship-path explanations over legacy semantic summaries.
  if (impact.description) {
    evidence.explanation = impact.description;
  } else if (impact.semantic_impact?.description) {
    evidence.explanation = impact.semantic_impact.description;
  }
  
  return evidence;
}

/**
 * Get human-readable type label
 */
export function getTypeLabel(impact) {
  return IMPACT_TYPE_LABELS[impact.type] || impact.type || 'Unknown';
}

/**
 * Get impact level color
 */
export function getImpactLevelColor(impactLevel) {
  return IMPACT_LEVEL_COLORS[impactLevel] || 'default';
}

